import React, { MouseEvent, useEffect, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/buttons';
import IconDelete from 'components/icons/delete';
import IconFile from 'components/icons/file';
import IconUpload from 'components/icons/upload';
import OptionallyVisible from 'components/optionallyVisible';

import { IMAGE_FILE_TYPES, LABELS } from './keys';
import { Caption, Container, ControlPanel, ErrorMessage, FileName, FilePreview, Hint, Hints, Icon, Input } from './styles';

export interface FileDropzoneProps {
  className?: string;
  disabled?: boolean;
  onFileChange?: (files: File[]) => void;
  filledInputLabel?: string;
  maxFileSize?: number;
  maxFileSizeInMB?: number;
  accept?: Accept;
  hints?: string[];
  initialFile?: File | string;
  error?: string;
}

interface DisplayedFile {
  URL: string;
  name: string;
  size?: string;
}

const decorateFiles = (files: (File | string)[]): DisplayedFile[] =>
  files.map((fileRecord) => {
    if (typeof fileRecord === 'string') {
      // Skip placeholder strings from sessionStorage
      if (fileRecord.startsWith('[FILE:') && fileRecord.endsWith(']')) {
        const fileName = fileRecord.slice(6, -1); // Extract filename from [FILE:name]
        return {
          URL: '',
          name: fileName,
        };
      }

      const [name] = fileRecord.split('/').reverse();

      return {
        URL: fileRecord,
        name,
      };
    }

    if (!IMAGE_FILE_TYPES.includes(fileRecord.type)) {
      return {
        URL: '',
        name: fileRecord.name,
        size: fileRecord.size?.toString(),
      };
    }

    return {
      URL: URL.createObjectURL(fileRecord),
      name: fileRecord.name,
      size: fileRecord.size?.toString(),
    };
  });

export const FileDropzone = ({
  initialFile,
  className,
  disabled,
  onFileChange = (nextValue: File[]) => {},
  maxFileSize,
  maxFileSizeInMB,
  accept,
  hints,
  error,
}: FileDropzoneProps) => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<DisplayedFile[]>(decorateFiles(initialFile ? [initialFile] : []));
  const [validationError, setValidationError] = useState<string | undefined>();

  // If maxFileSizeInMB is provided, convert it to bytes; otherwise use maxFileSize
  const maxSizeInBytes = maxFileSizeInMB ? maxFileSizeInMB * 1024 * 1024 : maxFileSize;
  const maxSizeMBDisplay = maxFileSizeInMB || (maxFileSize ? (maxFileSize / (1024 * 1024)).toFixed(1) : '1');

  const onDrop = (files: File[]) => {
    if (!files.length) {
      return;
    }

    setSelectedFiles(decorateFiles(files));
    setValidationError(undefined);
    onFileChange(files);
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      const errorCode = rejection.errors[0]?.code;

      if (errorCode === 'file-too-large') {
        setValidationError(`File size exceeds the maximum limit of ${maxSizeMBDisplay}MB`);
      } else if (errorCode === 'file-invalid-type') {
        setValidationError('File type is not supported');
      } else {
        setValidationError('File upload failed. Please try again.');
      }
    }
  };

  useEffect(() => {
    // Don't clear validation errors when initial file is just a placeholder from sessionStorage
    const isPlaceholder = typeof initialFile === 'string' && initialFile.startsWith('[FILE:') && initialFile.endsWith(']');

    setSelectedFiles(decorateFiles(initialFile ? [initialFile] : []));

    // Only clear validation error if this is a real file change, not a placeholder
    if (!isPlaceholder) {
      setValidationError(undefined);
    }
  }, [initialFile]);

  const { getRootProps, getInputProps, open, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    onDropRejected,
    accept,
    disabled,
    maxSize: maxSizeInBytes,
  });

  const renderFilePreview = (file: DisplayedFile) => {
    if (!file.URL) {
      return (
        <>
          <IconFile />
          <FileName>{file.name}</FileName>
        </>
      );
    }

    return <FilePreview src={file.URL} key={file.name} />;
  };

  const dropzoneText = t(LABELS.DRAG_AND_DROP_TEXT_EMPTY);
  const containerProps = getRootProps({ isFocused, isDragAccept, isDragReject });
  const inputProps = getInputProps();

  const preventPropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleEditClick = (event: MouseEvent) => {
    preventPropagation(event);
    open();
  };

  const handleDeleteClick = (event: MouseEvent) => {
    preventPropagation(event);
    setSelectedFiles([]);
    setValidationError(undefined);
    onFileChange([]);
  };

  const displayError = validationError || error;

  return (
    <>
      <Container {...containerProps} className={className} disabled={disabled} error={displayError}>
        <ControlPanel onClick={preventPropagation} active={Boolean(selectedFiles.length) && !disabled}>
          <Button secondary onClick={handleEditClick}>
            <IconUpload width={14} height={14} />
            <span>{t(LABELS.EDIT)}</span>
          </Button>
          <Button secondary onClick={handleDeleteClick}>
            <IconDelete width={18} height={18} />
            <span>{t(LABELS.DELETE)}</span>
          </Button>
        </ControlPanel>
        <Input {...inputProps} />
        <OptionallyVisible visible={Boolean(selectedFiles.length)}>{selectedFiles.map(renderFilePreview)}</OptionallyVisible>
        <OptionallyVisible visible={!selectedFiles.length}>
          <Icon>
            <IconUpload />
          </Icon>
          <Caption>{dropzoneText}</Caption>
        </OptionallyVisible>
      </Container>
      <Hints visible={Boolean(hints?.length)}>{hints?.map((hint) => <Hint key={hint}>{hint}</Hint>)}</Hints>
      <OptionallyVisible visible={Boolean(displayError)}>
        <ErrorMessage>{displayError}</ErrorMessage>
      </OptionallyVisible>
    </>
  );
};

export default FileDropzone;
